/**
 * OrgChartPage (portal del colaborador) — «quién es quién» en la empresa.
 *
 * ⚠️ Tres diferencias con el organigrama del ERP que son el diseño, no presentación:
 *
 *  1. **Campos mínimos**: nombre, cargo y cuántas personas le reportan. Sin RUT ni datos
 *     de contacto — bajo la Ley 21.719 el tratamiento tiene que ser proporcional a la
 *     finalidad, y para saber quién es quién alcanza con eso.
 *  2. **Sin los huecos administrativos**: quién no tiene jefatura y qué jefaturas están
 *     en ciclo es información de gestión para RRHH.
 *  3. **El nodo propio se destaca**, junto con su cadena de mando hacia arriba: es lo que
 *     la persona entra a buscar.
 *
 * ⚠️ Los datos van por `usePortalOrgChart`, que elige el cliente según el caso de
 * autenticación. Antes se pedían siempre con el del ERP y con una cuenta del portal
 * la pantalla no cargaba nunca.
 *
 * Y puede estar apagado por tenant (`org_chart_visible_to_employees`): en ese caso el
 * backend responde 403 y acá se explica, en vez de mostrar una pantalla vacía.
 */
import { useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, Network } from 'lucide-react'

import { Avatar } from '@/components/ds/Avatar'
import { EmptyState } from '@/components/ds/EmptyState'
import { GlassPanel } from '@/components/ds/GlassPanel'
import { SearchInput } from '@/components/ds/SearchInput'
import { Skeleton } from '@/components/ds/Skeleton'
import { usePortalOrgChart, type PortalOrgNode } from '../hooks/useEmployeePortal'
import { PaginaPortal } from '../components/PaginaPortal'

type PortalNode = PortalOrgNode

const norm = (s: string) =>
  s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()

function coincide(n: PortalNode, q: string): boolean {
  if (!q) return true
  if (norm(`${n.name} ${n.position}`).includes(q)) return true
  return n.children.some(c => coincide(c, q))
}

function Nodo({ nodo, q, colapsados, alternar }: {
  nodo: PortalNode
  q: string
  colapsados: Set<string>
  alternar: (id: string) => void
}) {
  const tieneHijos = nodo.children.length > 0
  // Mi cadena de mando arranca abierta: es lo que vine a ver.
  const abierto = q ? true : !colapsados.has(nodo.id)
  const resaltado = !!q && norm(`${nodo.name} ${nodo.position}`).includes(q)
  const hijos = q ? nodo.children.filter(c => coincide(c, q)) : nodo.children

  return (
    <li>
      <div className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 bg-surface ${
        nodo.is_me
          ? 'border-primary ring-1 ring-primary'
          : resaltado
            ? 'border-primary'
            : nodo.in_my_chain
              ? 'border-primary/40'
              : 'border-divider'
      }`}>
        {tieneHijos ? (
          <button
            type="button"
            onClick={() => alternar(nodo.id)}
            className="text-content-subtle hover:text-content"
            aria-label={`${abierto ? 'Colapsar' : 'Expandir'} el equipo de ${nodo.name}`}
            aria-expanded={abierto}
          >
            {abierto ? <ChevronDown size={16} aria-hidden="true" /> : <ChevronRight size={16} aria-hidden="true" />}
          </button>
        ) : (
          <span className="w-4" aria-hidden="true" />
        )}

        <Avatar nombre={nodo.name} fotoUrl={nodo.photo_url} tamano="sm" decorativa />

        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-content truncate">
            {nodo.name}
            {nodo.is_me && <span className="ml-2 text-xs font-medium text-primary">(yo)</span>}
          </p>
          <p className="text-xs text-content-muted truncate">
            {nodo.position || 'Sin cargo registrado'}
          </p>
        </div>

        {tieneHijos && (
          <p className="shrink-0 text-xs text-content-muted whitespace-nowrap">
            {nodo.subordinate_count} {nodo.subordinate_count === 1 ? 'persona' : 'personas'}
          </p>
        )}
      </div>

      {tieneHijos && abierto && (
        <ul className="ml-5 mt-2 space-y-2 border-l border-divider pl-4">
          {hijos.map(h => (
            <Nodo key={h.id} nodo={h} q={q} colapsados={colapsados} alternar={alternar} />
          ))}
        </ul>
      )}
    </li>
  )
}

export default function OrgChartPage() {
  const { data: datos, isLoading, error } = usePortalOrgChart()
  const apagado = (error as any)?.response?.status === 403
  const [busqueda, setBusqueda] = useState('')
  const [colapsados, setColapsados] = useState<Set<string>>(new Set())

  const q = norm(busqueda.trim())
  const raices = useMemo(
    () => (datos?.roots || []).filter(n => coincide(n, q)),
    [datos, q],
  )

  const alternar = (id: string) => setColapsados(prev => {
    const s = new Set(prev)
    if (s.has(id)) s.delete(id); else s.add(id)
    return s
  })

  return (
    <PaginaPortal
      titulo="Organigrama"
      bajada="Quién es quién en la empresa. La posición propia aparece destacada."
      ancho="angosto"
      acciones={datos && !apagado ? (
        <>
          <SearchInput
            label="Buscar persona o cargo"
            placeholder="Buscar persona o cargo…"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            className="w-64 max-w-full"
          />
          <span className="text-xs text-content-muted">{datos.total} personas</span>
        </>
      ) : undefined}
    >
      {isLoading ? (
        <GlassPanel><Skeleton variant="block" /></GlassPanel>
      ) : apagado ? (
        <EmptyState
          icon={<Network size={28} aria-hidden="true" />}
          title="La empresa no publica el organigrama"
          description="Para saber a quién dirigirse, se puede consultar con Recursos Humanos."
        />
      ) : error || !datos ? (
        <EmptyState variant="error" title="No se pudo cargar el organigrama" />
      ) : raices.length === 0 ? (
        busqueda
          ? <EmptyState variant="filtered" title="Sin resultados" description={`Nadie coincide con «${busqueda}».`} />
          : <EmptyState title="Todavía no hay estructura registrada" description="RRHH todavía no carga las jefaturas." />
      ) : (
        <GlassPanel as="section" aria-label="Estructura de la empresa">
          <ul className="space-y-2">
            {raices.map(n => (
              <Nodo key={n.id} nodo={n} q={q} colapsados={colapsados} alternar={alternar} />
            ))}
          </ul>
        </GlassPanel>
      )}
    </PaginaPortal>
  )
}
